export interface TenantSchedulerStatus {
  scheduler_type: string;
  status: string;
  last_start_time: string | null;
  last_completion_time: string | null;
  last_error_time: string | null;
  last_error_message: string | null;
  processing_count: number;
  success_count: number;
  error_count: number;
  is_processing: boolean;
}

export interface TenantCosts {
  total_cost: number;
  last_updated: string;
  cost_breakdown: Record<string, number>;
}

export interface HealthHistoryEntry {
  timestamp: string;
  status: string;
  cpu_percent: number;
  memory_percent: number;
  disk_percent: number;
  load_avg_1m: number;
}

export interface HealthMaxMetrics {
  cpu_percent: number;
  memory_percent: number;
  disk_percent: number;
}

export interface LoadAvg {
  '1m': number;
  '5m': number;
  '15m': number;
}

export interface CpuMetrics {
  percent: number | null;
  count: number;
}

export interface MemoryMetrics {
  percent: number;
  total: number;
  available: number;
  used: number;
  free: number;
}

export interface DiskMetrics {
  mountpoint: string;
  percent: number;
  total: number;
  used: number;
  free: number;
}

export interface MetricsSummary {
  load_avg: LoadAvg;
  cpu: CpuMetrics;
  memory: MemoryMetrics;
  disk: DiskMetrics;
  load_avg_per_cpu: LoadAvg;
}

export interface SystemHealth {
  status: string;
  last_check: string;
  last_unhealthy_at: string | null;
  consecutive_failures: number;
  metrics_summary: MetricsSummary;
  history: HealthHistoryEntry[];
  max_7d: HealthMaxMetrics;
}

/** Plugin info as reported by the running email agent in tenant status */
export interface LoadedPlugin {
  plugin_id: string;
  version: string;
  schedulers: string[];
  workflow_hooks: string[];
  catalog_mappers: string[];
  /** Present when reported by the agent; omitted on older API responses */
  document_types?: string[];
}

export interface TenantStatus {
  tenant_id: string;
  is_active: boolean;
  agent_version?: string;
  created_at: string;
  updated_at: string;
  last_activity: string | null;
  loaded_plugins?: LoadedPlugin[];
  /**
   * Public origin of the tenant's own container (`https://host`, no path),
   * registered by its local server at startup. Null until then. The admin
   * app forwards tenant-scoped calls there; the chat socket and the
   * "Open portal" link use it directly.
   */
  container_url?: string | null;
  schedulers: Record<string, TenantSchedulerStatus>;
  errors: string[];
  error_count: number;
  critical_error_count: number;
  costs: TenantCosts;
  total_tasks_processed: number;
  total_emails_processed: number;
  average_processing_time_seconds: number;
  metadata: Record<string, unknown>;
  health?: SystemHealth;
}

export interface TenantStatusResponse {
  message: string;
  tenant_statuses: TenantStatus[];
  count: number;
}

// Email-related types moved to src/components/tenant/inbound/email/types.tsx

// Tenant Configuration types
export interface TenantIdentity {
  tenant_id: string;
  tenant_name: string;
  tenant_email_domain: string;
  agent_name: string;
}

export interface EmailFolders {
  start_folder: string;
  processing_folder: string;
  successfully_processed_folder: string;
  awaiting_manual_processing_folder: string;
  awaiting_information_folder?: string;
  ir_replies_processed_folder?: string;
}

export interface AIModels {
  document_parsing_model: string;
  email_classifier_model: string;
  select_customer_model: string;
  select_product_model: string;
  verify_product_model: string;
  extract_keywords_model: string;
  conversation_model: string;
  conversation_model_prompt_id: string;
  rule_generator_model?: string;
  quote_notes_evaluation_model: string;
  quote_designation_judge_model: string;
  /** Problem analysis: possible causes of a case; runs at reasoning effort low. */
  problem_analysis_model?: string;
  /** Applies remembered customer notes to a parsed document (the
   *  interpretation step), on every order with a candidate note and in the
   *  teach proof. Its own slot so the chat model can stay small. */
  customer_notes_model?: string;
  /** Navigator phase 3 — vision-LLM consolidation. */
  navigator_consolidation_model?: string;
  /** Navigator phase 4 — PDP-discovery fallback. */
  navigator_pdp_fallback_model?: string;
  /** Navigator per-supplier LLM call cap, shared across all research phases. */
  navigator_max_calls_per_supplier?: number;
}

export interface EmbeddingModels {
  product_search_model: string;
}

/** Product retrieval strategy. Full set — no third value planned. */
export type ProductSearchStrategy = 'hybrid_vector' | 'keyword';

/** Only backend value today; kept as a union so a second provider is a type error to forget. */
export type RerankerProvider = 'voyage';

/**
 * Cross-encoder reranking of the multi-query keyword leg's candidates.
 * Absent => disabled (backend default): reciprocal-rank fusion keeps the ordering.
 */
export interface Reranker {
  /** Backend rejects `true` without `multi_query_keyword` — the reranker orders that leg only. */
  enabled?: boolean;
  provider?: RerankerProvider;
  /** Backend rejects an empty model while enabled (every call would fail into the RRF fallback). */
  model?: string;
  /** Optional relevance hint for instruction-following rerankers (rerank-2.5). Empty = none. */
  instruction?: string;
}

export interface ProductSearch {
  strategy: ProductSearchStrategy;
  /** Keyword-only enhancement: expand a query into multiple keyword sub-queries. Ignored for hybrid. */
  multi_query_keyword?: boolean;
  reranker?: Reranker;
}

export interface PDFProcessing {
  use_docling: boolean;
  use_anthropic_pdf_parser: boolean;
  /**
   * When true, route PDF → Markdown through a vision-capable model that ingests
   * rendered pages/images. This does not change `ai_models.document_parsing_model`.
   */
  use_llm_vision_for_md?: boolean;
  /** Model used when `use_llm_vision_for_md` is enabled. */
  llm_vision_md_model?: string;
  /**
   * When true, route PDF → Markdown through a model that ingests the raw PDF.
   * This does not change `ai_models.document_parsing_model`.
   */
  use_llm_pdf_for_md?: boolean;
  /** Model used when `use_llm_pdf_for_md` is enabled. */
  llm_pdf_md_model?: string;
  zoom_factor?: number;
}

// Realistic subset of core ProcessType values for the OTHER-fallback dropdown.
// Plugin-contributed ProcessType values are intentionally not allowed by the
// admin-server for `fallback_process_type`, so the dropdown is hardcoded to
// the core subset that maps cleanly to dedicated single-type mailboxes.
export const FALLBACK_PROCESS_TYPES = [
  'sales_order',
  'product_description'
] as const;

export type FallbackProcessType = (typeof FALLBACK_PROCESS_TYPES)[number];

export interface EmailProcessing {
  /**
   * When true, OCR inline images referenced via `cid:...` in HTML email bodies
   * and inline the extracted text into the markdown passed to the classifier
   * and parser. Lets the LLM "read" orders that arrive as embedded screenshots.
   */
  enable_inline_image_ocr?: boolean;
  /**
   * Fallback email type used when the LLM classifier returns `OTHER`. Confident
   * non-OTHER classifications and guardrail downgrades (e.g.
   * SALES_ORDER → UNSUPPORTED_SALES_ORDER) are preserved unchanged.
   * `null` / absent = feature off.
   */
  fallback_process_type?: FallbackProcessType | null;
  /**
   * When true, classifier-flagged conversation emails (replies / thank-you /
   * back-and-forth) are moved to the manual-processing folder and skipped
   * without notifying anyone. They land as COMPLETED with
   * `additional_data.ignored === true`. Default `false`.
   */
  ignore_conversations?: boolean;
}

export interface MicrosoftGraphAPI {
  client_id: string;
  client_secret: string;
  account: string;
  tenant_id: string;
}

/** One value-mapping rule: any of `match` (semantic labels the LLM emits) resolves to `code` (the BC code). */
export interface BusinessCentralCodeMappingRule {
  match: string[];
  code: string;
}

/** Rules for a single BC code field (e.g. `shippingAgentCode`). First matching rule wins. */
export interface BusinessCentralCodeMappingField {
  rules: BusinessCentralCodeMappingRule[];
}

export interface BusinessCentral {
  tenant_id: string;
  environment: string;
  company_id: string;
  client_id: string;
  client_secret: string;
  api_publisher: string;
  api_group: string;
  api_version: string;
  /**
   * OData $expand navigation properties fetched with each BC product/item.
   * Default `['itemTranslations', 'itemAdditionalValues']`; empty fetches neither.
   */
  product_expand?: string[];
  /** Tenant-configured value maps keyed by BC field name (e.g. `shippingAgentCode`). */
  code_mappings?: Record<string, BusinessCentralCodeMappingField>;
  /**
   * Create an order's delivery address in BC when it matches none of the
   * customer's ship-to addresses, and send the code BC returns.
   *
   * Writes into master data. BC assigns the code, does not de-duplicate and
   * offers no delete, so a wrongly created address is permanent and becomes a
   * match candidate for every later order of that customer. Absent = off.
   */
  create_missing_ship_to_addresses?: boolean;
}

/** SAP IDoc content settings */
export interface SapIdocConfig {
  idoc_generator?: string;
  sender_port?: string;
  sender_partner_type?: string;
  sender_partner_function_code?: string;
  sender_partner_number?: string;
  receiver_port?: string;
  receiver_partner_type?: string;
  receiver_partner_function_code?: string;
  receiver_partner_number?: string;
  sales_org?: string;
  distribution_channel?: string;
  division?: string;
  order_type?: string;
  currency?: string;
  end_customer_number?: string;
  cimtyp?: string;
  quote_reference_qualf?: string;
  suppress_country_for_override_addresses?: boolean;
  omit_partner_number_for_override_addresses?: boolean;
  quote_reference_level?: 'header' | 'position';
  test_mode?: boolean;
  /** When true (default), E1EDP01 includes VPREI, PEINH, CURCY per line when price info exists. */
  include_line_item_prices?: boolean;
  error_number_mapping?: Record<string, string>;
}

/** SAP IDoc HTTP transport settings */
export interface SapIdocTransportHttp {
  url?: string;
  api_key?: string;
  username?: string;
  password?: string;
  /** How to send the IDoc XML: 'multipart' for file upload (form-data), 'xml' for raw XML POST */
  content_type?: 'multipart' | 'xml';
}

/** SAP IDoc email transport settings */
export interface SapIdocTransportEmail {
  email_recipient?: string;
}

/** SAP IDoc transport configuration */
export interface SapIdocTransport {
  http?: SapIdocTransportHttp;
  email?: SapIdocTransportEmail;
}

export interface SapCatalogTransportHttp {
  url?: string;
  api_key?: string;
  username?: string;
  password?: string;
}

export interface SapCatalogTransport {
  http?: SapCatalogTransportHttp;
}

export interface SapConnector {
  idoc_transport?: SapIdocTransport;
  catalog_transport?: SapCatalogTransport;
}

export interface GmailAPI {
  // New approach: Store JSON content as encrypted strings (recommended)
  oauth2_credentials_json: string | null;
  service_account_json: string | null;
  
  // Legacy approach: File paths (deprecated, maintained for backward compatibility)
  oauth2_credentials_path: string | null;
  service_account_path: string | null;
  
  account: string | null;
}

export interface Notifications {
  task_report_recipient: string;
}

export interface SchedulerConfig {
  enabled: boolean;
  interval_seconds: number;
  batch_size?: number;
  /** Minute of the hour (0-59) to align runs to; unset = plain interval scheduling. */
  run_at_minute?: number;
}

/** A single scheduler provided by a plugin */
export interface PluginSchedulerInfo {
  name: string;
  default_interval_seconds: number;
}

/** A workflow hook contributed by a plugin */
export interface PluginWorkflowHook {
  hook_point: string;
  has_enabled_checker: boolean;
}

/** A quote mapper contributed by a plugin */
export interface PluginCatalogMapper {
  name: string;
}

/** A plugin descriptor as returned by GET /api/plugins/available */
/** A plugin descriptor as returned by GET /api/plugins/available */
export interface AvailablePlugin {
  plugin_id: string;
  version: string;
  schedulers: (PluginSchedulerInfo | string)[];
  workflow_hooks?: (PluginWorkflowHook | string)[];
  catalog_mappers?: (PluginCatalogMapper | string)[];
}

export interface QuoteCatalogConfig {
  mapper?: string;
  header_sheet_name?: string;
  items_sheet_name?: string;
  filename?: string;
  integration_type?: string;
  /** Delta sync deletes stale quotes hourly; default false = additive-only,
   *  stale quotes are left for the nightly rebuild. */
  delta_apply_deletes?: boolean;
  /** Delta sync delete guard: refuse to delete more than this share of stored
   *  quotes (0..1). Only evaluated when delta_apply_deletes is true. */
  delta_max_delete_fraction?: number;
  /** Delta sync delete guard only fires above this absolute delete count.
   *  Only evaluated when delta_apply_deletes is true. */
  delta_min_delete_guard_count?: number;
}

export interface CustomerCatalogConfig {
  integration_type?: string;
  /** Legacy HTTP fetch config; integration_type is the successor. Either one
   *  makes the catalog eligible for HTTP fetch (and its delta pass). */
  http_source?: HttpFileSourceConfig;
  filename?: string;
  sheet_name?: string;
  phone_country_code?: string;
  required_source_columns?: string[];
  column_mapping?: Record<string, string>;
  role_emails?: CustomerCatalogRoleEmailsConfig;
  /** Delta sync deletes stale customers hourly; default false = additive-only,
   *  stale customers are left for the nightly rebuild. */
  delta_apply_deletes?: boolean;
  /** Delta sync delete guard: refuse to delete more than this share of stored
   *  customers (0..1). Only evaluated when delta_apply_deletes is true. */
  delta_max_delete_fraction?: number;
  /** Delta sync delete guard only fires above this absolute delete count.
   *  Only evaluated when delta_apply_deletes is true. */
  delta_min_delete_guard_count?: number;
}

export const CANONICAL_DOCUMENT_EMAIL_ROLES = [
  'invoice',
  'order_confirmation',
  'delivery_notification',
] as const;

export type CanonicalDocumentEmailRole = (typeof CANONICAL_DOCUMENT_EMAIL_ROLES)[number];

export interface CustomerCatalogRoleEmailsConfig {
  sheet_name: string;
  customer_number_column: string;
  email_column: string;
  role_column: string;
  role_mapping: Record<string, CanonicalDocumentEmailRole>;
}

export interface HttpFileSourceConfig {
  integration_type?: string;
  url?: string;
  api_key?: string;
  filename?: string;
}

export interface CustomerRoleLinkColumnMapping {
  source_customer_number_column?: string;
  target_customer_number_column?: string;
  role_type_column?: string;
  is_default_column?: string;
  sales_org_column?: string;
  distribution_channel_column?: string;
  division_column?: string;
}

export interface CustomerRoleLinkIngestionConfig {
  integration_type?: string;
  filename?: string;
  sheet_name?: string;
  http_source?: HttpFileSourceConfig;
  column_mapping?: CustomerRoleLinkColumnMapping;
  row_filters?: Record<string, string[]>;
  /** Delta sync deletes stale role links hourly; default false = additive-only,
   *  stale links are left for the nightly rebuild. */
  delta_apply_deletes?: boolean;
  /** Delta sync delete guard: refuse to delete more than this share of stored
   *  role links (0..1). Only evaluated when delta_apply_deletes is true. */
  delta_max_delete_fraction?: number;
  /** Delta sync delete guard only fires above this absolute delete count.
   *  Only evaluated when delta_apply_deletes is true. */
  delta_min_delete_guard_count?: number;
}

export interface CustomerContactPersonsConfig {
  integration_type?: string;
  filename?: string;
  sheet_name?: string;
  required_source_columns?: string[];
  /** Nested mapping supported (e.g. name.source_columns). */
  column_mapping?: Record<string, any>;
}

export const CONTRACT_MODEL_FIELDS = [
  'contract_number',
  'customer_number',
  'article_number_supplier',
] as const;
export type ContractModelField = (typeof CONTRACT_MODEL_FIELDS)[number];

export interface ContractCatalogConfig {
  integration_type?: string;
  filename?: string;
  sheet_name?: string;
  required_source_columns?: string[];
  column_mapping?: { columns: Record<string, string> };
}

export interface ProductCatalogConfig {
  /**
   * Source-column → canonical-field mapping for product imports, parallel to the
   * customer/contract catalog mappings. A canonical field left empty (or omitted)
   * keeps its identifier slot empty, so that identifier tier never matches on it.
   * e.g. { columns: { manufacturer_article_number: 'description2' } }.
   */
  column_mapping?: { columns: Record<string, string> };
  /**
   * Config-driven article-number aliases, e.g. an Eldas number read from a
   * Business Central additional value: [{ namespace: 'eldas', source: { additional_value: 'Eldas No. ASC' } }].
   * The source is a flat item field name, a composite mapping, or { additional_value }.
   */
  additional_article_numbers?: ArticleNumberAliasMapping[];
}

/** Mirrors the backend `ArticleNumberAliasMapping`: a flat BC item field name, a
 *  composite mapping (`source_columns`, `mode`, `separator`), or a BC additional value. */
export interface ArticleNumberAliasMapping {
  namespace: string;
  source:
    | string
    | { additional_value: string }
    | { source_columns: string[]; mode?: 'join' | 'first_non_empty'; separator?: string };
}

export interface DataIngestion {
  quote_catalog?: QuoteCatalogConfig;
  customer_catalog?: CustomerCatalogConfig;
  customer_role_links?: CustomerRoleLinkIngestionConfig;
  customer_contact_persons?: CustomerContactPersonsConfig;
  contract_catalog?: ContractCatalogConfig;
  product_catalog?: ProductCatalogConfig;
}

export interface CustomerMasterSyncIntegrity {
  total_role_links: number;
  orphaned_source_count: number;
  orphaned_target_count: number;
}

export interface CustomerMasterSyncResponse {
  tenant_id: string;
  customers_imported: boolean;
  role_links_imported: boolean;
  integrity: CustomerMasterSyncIntegrity | null;
}

export interface Voice {
  id: string;
  name: string;
  provider: string;
  language_code: string;
  language_name: string;
  gender: string;
  category: string;
  age_group: string;
  accent: string;
  description: string;
}

export interface VoiceDatabaseResponse {
  message: string;
  voices: Voice[];
}

export interface ConversationPrompt {
  role: string;
  begin_sentence: string;
  system_prompt: string;
  agent_prompt: string;
  reminder_prompt: string;
}

export interface ElevenLabsConfig {
  voice_id?: string;
  model?: string;
  speed?: number;
  stability?: number;
  similarity_boost?: number;
  elevenlabs_text_normalization?: string;
}

export interface TTSVoiceConfig {
  provider?: string;
  elevenlabs_config?: ElevenLabsConfig;
}

export interface TwilioVoiceAgent {
  interruptible?: boolean;
  welcome_greeting?: string;
  hints?: string;
  language?: string;
  stt_provider?: string;
  tts_voice_config?: TTSVoiceConfig;
  parameters?: Record<string, any>;
  conversation_prompts?: Record<string, ConversationPrompt>;
}

export interface Connectors {
  microsoft_graph_api: MicrosoftGraphAPI;
  business_central: BusinessCentral;
  sap?: SapConnector;
  gmail_api: GmailAPI;
  exchange_on_prem?: MicrosoftGraphAPI;
}

export interface AttributeLookup {
  source_field: string;
  lookup_namespace: string;
  target_attribute: string;
  key_normalization?: string;
}

export interface DocumentIntegration {
  integration_type: string;
  enabled: boolean;
  validation_method?: "catalog" | "quote";
  requires_confirmation_as_work_description?: boolean;
  /**
   * Weekday a Kalenderwoche delivery date (e.g. "KW 28/26") resolves to.
   * 0 = Monday (earliest, default) … 6 = Sunday.
   */
  calendar_week_delivery_weekday?: number;
  /**
   * How a Kalenderwoche range (e.g. "KW 28-30") resolves:
   * `start` = first week (earliest, default); `end` = last week (deadline).
   */
  calendar_week_range_policy?: "start" | "end";
  /**
   * When True, add an informational work description each time a
   * Kalenderwoche delivery date is resolved to a concrete day.
   */
  calendar_week_resolved_as_work_description?: boolean;
  /**
   * Days after the arrival date an ASAP delivery term (asap, sofort,
   * schnellstmöglich) resolves to. 0 = arrival day (default), 1 = next day.
   */
  asap_delivery_offset_days?: number;
  /**
   * When True (default), add an informational work description each time an
   * ASAP delivery term is resolved to a concrete day.
   */
  asap_delivery_resolved_as_work_description?: boolean;
  /**
   * Output mode for product_description integrations.
   * `refined` (default) runs the full pipeline; `raw` parses + LLM-judges only
   * and skips enrichment/refinement/meta-tags/post-processing.
   */
  output_mode?: "refined" | "raw";
  enrichment_enabled?: boolean;
  web_search_fallback_enabled?: boolean;
  refinement_batch_size?: number;
  swap_eszett?: boolean;
  url_template?: string;
  url_slug_stopword_languages?: string[];
  attribute_lookups?: AttributeLookup[];
  /**
   * Name of the category registry the parser loads for product descriptions.
   * Only meaningful on `product_description`. Set via the Category Registry
   * page's "Für Parsing aktivieren" action (partial config write).
   */
  category_registry_ref?: string;
  config: Record<string, any>;
  /**
   * Optional SAP IDoc configuration for order integrations.
   * Only populated when integration_type is a SAP IDoc variant
   * (e.g. sap_idoc_http, sap_idoc_email).
   */
  idoc?: SapIdocConfig;
}

export interface DocumentIntegrations {
  order?: DocumentIntegration;
  order_confirmation?: DocumentIntegration;
  backorder_report?: DocumentIntegration;
  incoming_quote?: DocumentIntegration;
  product_description?: DocumentIntegration;
}

/** Script entry: server and UI use only type, enabled, script. */
export interface ScriptEntry {
  type: string;
  enabled: boolean;
  script: string;
}

export interface CustomerDisambiguation extends ScriptEntry {}

export interface CustomerFilter extends ScriptEntry {}

export interface ProductDisambiguation extends ScriptEntry {}

export interface ProductFilter extends ScriptEntry {}

/** Identifies one of the per-tenant CEL search-rule scripts. */
export type ScriptKind =
  | 'customer_filter'
  | 'customer_disambiguation'
  | 'product_filter'
  | 'product_disambiguation';

export interface Scripts {
  customer_disambiguation?: CustomerDisambiguation;
  customer_filter?: CustomerFilter;
  customer_search_strategy_order?: string[];
  product_disambiguation?: ProductDisambiguation;
  product_filter?: ProductFilter;
  quote_matching?: QuoteMatchingConfig;
}

export interface DocumentRuleAction {
  // 'set_field' writes a computed value to `path`; 'add_work_description'
  // appends a note (code + message from value_expression) to the document's
  // work-description ledger and ignores `path`.
  type: string;
  path?: string;
  value_expression: string;
  code?: string;
  reason?: string;
}

// Provenance of a rule. `taught` = authored in chat by a person via explicit
// teaching (`save_rule`); anything else (`manual`, legacy `generated`, unset) is
// treated as hand-configured. Kept a loose string because the backend does not
// enforce an enum — handle unknown values gracefully.
export type RuleSource = 'taught' | 'manual' | string;

export interface DocumentRule {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  priority: number;
  engine_type: string;
  condition: string;
  actions: DocumentRuleAction[];
  original_description?: string;
  learning_record_id?: string;
  // Explicit-teaching provenance (config passthrough; absent on older/manual rules).
  source?: RuleSource;
  created_by?: string;
  created_at?: string; // ISO-8601 string (model_dump(mode="json")), not a Date
}

export type QuoteRuleType = 'gate' | 'dimension';

export type BlockerDimension = 'price' | 'moq' | 'msq' | 'expiry' | 'contract' | 'other';

export interface QuoteCandidateRule {
  id: string;
  name?: string;
  description?: string;
  enabled: boolean;
  priority: number;
  condition: string;
  rejection_reason?: string;
  // Findings refactor (optional; unset = legacy auto-detect gate via gate_rule_id).
  // `gate` = eligibility predicate (failure drops the candidate); `dimension` =
  // closeness measurement scored for every candidate that passed all gates.
  rule_type?: QuoteRuleType;
  // Only meaningful for dimension rules; lets HITL / work_description_mapping target the
  // specific blocked dimension (e.g. f.details.closest.blocker_dimension == 'price').
  blocker_dimension?: BlockerDimension;
  // Declares that this gate establishes the quote belongs to the ordering customer.
  // Read by price reconciliation, which relies on the tenant's own identity semantics
  // instead of re-deriving them with a stricter exact-customer-number check. Only
  // meaningful on `rule_type: 'gate'` — the backend rejects it on a dimension at load.
  proves_customer_identity?: boolean;
}

export interface RankingCriterion {
  expression: string;
  order: 'asc' | 'desc';
}

export interface CandidateRanking {
  expression?: string;
  order?: 'asc' | 'desc';
  criteria?: RankingCriterion[];
}

// Whether a selected quote may corroborate a per-unit price the parser flagged as
// inconsistent and retract the resulting halt. Needs a gate rule marked
// `proves_customer_identity`; without one the backend falls back to exact
// customer-number matching.
export interface PriceReconciliationConfig {
  enabled: boolean;
}

export interface QuoteMatchingConfig {
  candidate_rules: QuoteCandidateRule[];
  candidate_ranking?: CandidateRanking;
  price_reconciliation?: PriceReconciliationConfig;
  // String mode, not a boolean; missing means 'off'.
  own_customer_first?: 'off' | 'live';
}

// Execution point of a document rule: 'parsed' runs after parsing (before
// matching/validation), 'processed' runs after matching & validation and
// additionally sees the processing result (matched_items, is_valid, …).
export type RulePhase = 'parsed' | 'processed';

export interface DocumentTypeRules {
  parsed: DocumentRule[];
  processed?: DocumentRule[];
}

export interface DocumentRules {
  [documentType: string]: DocumentTypeRules;
}

export interface PromptExtension {
  parsing?: string | null;
  validation?: string | null;
  classification?: string | null;
  refinement?: string | null;
  field_definitions?: Record<string, string> | null;
  blocked_work_description_patterns?: string[] | null;
  [key: string]:
    | string
    | null
    | undefined
    | Record<string, string>
    | string[];
}

export interface PromptExtensions {
  [documentType: string]: PromptExtension | null;
}

export type HitlAction = 'send_to_backend' | 'block_for_review' | 'request_info_sender' | 'request_info_expert' | 'forward_to_expert' | 'move_manual';

export type HitlRecipientSource = 'account_manager' | 'support_agent' | 'expert' | 'default_operator';

export type HitlReasonRole = 'router' | 'explainer';

// Early-evaluation point for a HITL rule. Only `customer_resolved` exists today
// (inside order validation, after customer/contact resolution, before quote
// matching); any other string is rejected at config load by the backend.
export type HitlCheckpoint = 'customer_resolved';

export interface HitlRule {
  id: string;
  name?: string;
  description?: string;
  enabled: boolean;
  document_type: string;
  condition: string;
  priority: number;
  // Optional early evaluation point. When set, the rule is also evaluated at the
  // named pipeline checkpoint; a match there with action `move_manual` skips
  // quote matching for the order. Unset = evaluated once at the end, as always.
  checkpoint?: HitlCheckpoint;
  // Optional link to procedural memory (auto-learn/replay). Renamed from the
  // legacy `resolution_type`; only used when procedural memory is enabled.
  procedure_family?: string;
  /** @deprecated renamed to `procedure_family`; kept for round-trip of legacy docs. */
  resolution_type?: string;
  action?: HitlAction;
  expert_email?: string;
  body_template?: string;
  subject_template?: string;
  work_description_template?: string;
  task_email?: boolean;
  recipients?: HitlRecipientSource[];
  // Findings refactor (optional; unset = legacy WD-based rule that both selects an
  // action and contributes narrative). `router` owns the action (action selection is
  // restricted to routers); `explainer` contributes to the operator narrative only
  // and ignores `action`. Required when the condition references the `findings` accessor.
  reason_role?: HitlReasonRole;
  // Explicit-teaching provenance (config passthrough; absent on older/manual rules).
  source?: RuleSource;
  created_by?: string;
  created_at?: string; // ISO-8601 string (model_dump(mode="json")), not a Date
  original_description?: string;
}

export interface InformationRequestRouting {
  target: 'sender' | 'expert';
  expert_email?: string | null;
}

export interface HitlInformationRequestConfig {
  enabled: boolean;
  max_rounds: number;
  reply_timeout_hours: number;
  eligible_document_types: string[];
  document_type_routing?: Record<string, InformationRequestRouting>;
}

export interface ManualEscalationNotification {
  task_email?: boolean;
  subject_template?: string;
  body_template?: string;
  recipients?: HitlRecipientSource[];
}

/**
 * Accepts two shapes:
 *  - Flat (legacy): ManualEscalationNotification fields at the top level.
 *  - Nested: `default` and/or `per_process_type` entries (each is a ManualEscalationNotification).
 * The presence of `default` or `per_process_type` indicates nested mode.
 */
export interface HitlFallbackEscalationConfig extends ManualEscalationNotification {
  default?: ManualEscalationNotification;
  per_process_type?: Record<string, ManualEscalationNotification>;
}

export interface HitlConfig {
  enabled: boolean;
  backend_edit_mode?: boolean;
  default_operator_email?: string;
  dry_run_recipient_send?: boolean;
  rules: HitlRule[];
  information_request?: HitlInformationRequestConfig;
  fallback_escalation?: HitlFallbackEscalationConfig;
}

export interface ResolutionMemoryConfig {
  enabled: boolean;
  max_candidates: number;
}

export interface MemoryConfig {
  resolution?: ResolutionMemoryConfig;
}

export type FindingOutcome = 'closest_match' | 'no_candidates' | 'all_gate_filtered' | 'matched';

// Equality-only nested matcher against a synthesized finding's `details`.
export interface FindingDetailsMatch {
  outcome: FindingOutcome;
  closest?: { blocker_dimension: BlockerDimension };
}

export type WorkDescriptionMappingLocale = 'de' | 'en';

export interface WorkDescriptionMappingEntry {
  // Legacy work-description key. Optional since the Findings refactor: an entry is
  // triggered by EITHER `key` (legacy) OR `finding_kind` (finding-shape), never both.
  key?: string;
  // Display language for this entry. Backend resolves by (trigger, locale) and
  // treats an omitted locale as "de"; falls back to canonical English on no match.
  locale?: WorkDescriptionMappingLocale;
  code: string;
  title?: string;
  message?: string;
  sender_prompt_hint?: string;
  expert_prompt_hint?: string;
  // Finding-shape trigger (post-refactor). When set, `key` must be absent.
  finding_kind?: string;
  finding_details_match?: FindingDetailsMatch;
  // Key-based twin of `finding_details_match`: exact-equality matcher against the
  // work description's `details`, so two entries can share a `key`. Not editable
  // in the UI; carried through edits and imports unchanged.
  details_match?: Record<string, unknown>;
  // Withhold the matching work description from every display sink (email
  // summary, report, case notices, ERP work description). Rules, traces and the
  // stored document still see it. Applies to all locales.
  suppress?: boolean;
}

export interface WorkDescriptionMappingConfig {
  entries: WorkDescriptionMappingEntry[];
}

/** Inventory replenishment agent settings (config block `replenishment`). */
export interface Replenishment {
  enabled: boolean;
  default_service_level: number;
  period_unit: string;
  language?: 'en' | 'de' | 'fr' | 'it';
  llm_escalation_mode?: 'gated' | 'all';
  supply_cap_months?: number;
  default_lead_time_periods?: number;
  escalation_recent_periods?: number;
  escalation_trend_sigma?: number;
  escalation_cov_threshold?: number;
  escalation_zero_share?: number;
}

/**
 * Leadgen decision-process settings (config block `leadgen`). Mirrors the
 * backend `LeadgenTenantConfig` — policy knobs only; invocation-level choices
 * (n, workbook, wave) stay CLI-only. `enabled` gates the chat tools and the
 * portal `leadgen` capability for the tenant's users.
 */
export interface LeadgenSettings {
  enabled: boolean;
  countries: string[];
  per_company: number;
  overgenerate_factor: number;
  research_model: string;
  verify_model: string;
  messages_model: string;
  persona_fallback_model: string;
  configs_dir: string;
}

/**
 * Customer-portal component ids: one per tab group (see
 * `components/portal/portalCapabilities.ts` for the tabs each one unlocks).
 */
export type PortalComponentId =
  | 'product_data'
  | 'order_data'
  | 'incoming_emails'
  | 'chat'
  | 'leadgen'
  | 'replenishment'
  | 'learning'
  | 'business_rules';

/**
 * Config block `portal`: what the tenant wants to see. Owner-editable. Partial
 * updates store only the keys written; a missing key means enabled.
 */
export interface PortalConfig {
  components: Partial<Record<PortalComponentId, boolean>>;
}

/**
 * Config block `license`: what we sold. Admin-only (the backend refuses a
 * non-admin write and never copies it in a config sync). A missing key means
 * licensed, except `chat` (the Assistant preview), which is unlicensed unless set.
 */
export interface LicenseConfig {
  portal: { components: Partial<Record<PortalComponentId, boolean>> };
}

export interface TenantConfiguration {
  identity: TenantIdentity;
  portal?: PortalConfig;
  license?: LicenseConfig;
  email_folders: EmailFolders;
  email_agent_connector_type?: string;
  ai_models: AIModels;
  embedding_models?: EmbeddingModels;
  // Absent => treat as { strategy: 'hybrid_vector' } (backend default). Never back-fill on load.
  product_search?: ProductSearch;
  pdf_processing: PDFProcessing;
  email_processing?: EmailProcessing;
  connectors: Connectors;
  document_integrations: DocumentIntegrations;
  notifications: Notifications;
  twilio_voice_agent: TwilioVoiceAgent;
  scripts?: Scripts;
  replenishment?: Replenishment;
  leadgen?: LeadgenSettings;
  schedulers?: Record<string, SchedulerConfig>;
  document_rules?: DocumentRules;
  prompt_extensions?: PromptExtensions;
  hitl?: HitlConfig;
  dialog?: import('./Dialog').DialogConfig;
  memory?: MemoryConfig;
  data_ingestion?: DataIngestion;
  plugins?: Record<string, Record<string, any>>;
  work_description_mapping?: WorkDescriptionMappingConfig;
  // Legacy alias coalesced into `work_description_mapping` on load and never
  // re-persisted. Present here only to type pre-normalize GET payloads.
  error_mapping?: WorkDescriptionMappingConfig;
}

export interface TenantConfigurationResponse {
  message: string;
  tenant_id: string;
  config: TenantConfiguration;
  status: string;
  /**
   * Concurrency token of the stored config (GET) or of the config just saved
   * (POST). Sent back as `base_hash` on the next save. Absent from servers that
   * predate it, and from a save whose read-back failed.
   */
  config_hash?: string;
}

// Tenant configuration list (GET /config/)
export interface TenantConfigurationListItem {
  tenant_id: string;
  config: TenantConfiguration;
}

export interface TenantConfigurationListResponse {
  message: string;
  tenant_configs: TenantConfigurationListItem[];
  count: number;
}

// Single tenant status (GET /tenant-status/{tenantId})
export interface SingleTenantStatusResponse {
  message: string;
  tenant_id: string;
  status: TenantStatus;
  found: boolean;
}

// Order-related types moved to src/components/tenant/outbound/orders/types.tsx

// Voice conversation-related types moved to src/components/tenant/inbound/voice/types.tsx


